import {
  Carousel,
  CarouselArrowButton,
  CarouselCore,
  CarouselHorizontalList,
  CarouselNext,
  CarouselPrevious,
  CarouselSlideItem,
  Icon,
  TextLink
} from '@cash-rewards/cr-ui-components';
import {
  ARRAY_ELEMENT_ID,
  EVENT_NAME,
  EventAttributes
} from '@cash-rewards/mparticlejs';
import { clsxm, extractUtmQuery } from '@cash-rewards/reactjs';
import {
  CarouselTrackingConfig,
  EventMapper,
  ProductMapper,
  useCarouselClickTracking,
  useCarouselImpressionTracking
} from '@cash-rewards/reactjs/client';
import { useRouter } from 'next/router';
import React, { useCallback } from 'react';

import useGetUserAgent from '@/hooks/useGetUserAgent';

interface ShopCarouselProps<T extends object> {
  children?: React.ReactElement | React.ReactElement[];
  title: string;
  navigationButtonTopCss: string;
  viewAllPath?: string;
  items: T[];
  productMappers: ProductMapper<T>[];
  clickEventMappers?: EventMapper<T>[];
  carouselType: ARRAY_ELEMENT_ID;
  customAttributeOverrides?: EventAttributes;
  impressionEventName?: EVENT_NAME;
  clickEventName?: EVENT_NAME;
}

const ShopCarousel = <T extends object>({
  children,
  title,
  navigationButtonTopCss,
  viewAllPath,
  items,
  productMappers,
  carouselType,
  clickEventMappers,
  customAttributeOverrides,
  impressionEventName = EVENT_NAME.impression_shophome_carousel,
  clickEventName = EVENT_NAME.click_web_merchant,
  ...rest
}: ShopCarouselProps<T>) => {
  const router = useRouter();
  const carousel = React.useRef<CarouselCore>();
  const { isMobile } = useGetUserAgent();

  const [, setIsCarouselAvailable] = React.useState(false);

  const attributeOverrides = {
    ...extractUtmQuery(router.query),
    ...customAttributeOverrides
  };

  const config: CarouselTrackingConfig = {
    device: isMobile ? 'mobile' : 'desktop'
  };

  const { onMounted } = useCarouselImpressionTracking(
    carouselType,
    items,
    productMappers,
    attributeOverrides,
    impressionEventName,
    config
  );

  const { logEvent } = useCarouselClickTracking(
    carouselType,
    productMappers,
    clickEventMappers,
    attributeOverrides,
    clickEventName,
    config
  );

  const handleCardClick = useCallback(
    (index: number) => {
      if (carousel.current) {
        const pageInfo = carousel.current?.getPageInfo();
        const item = items[index];

        if (carouselType === ARRAY_ELEMENT_ID.personalised_store) {
          logEvent(
            index,
            item,
            pageInfo.currentPage,
            pageInfo.itemsPerPage,
            items
          );
        } else {
          logEvent(index, item, pageInfo.currentPage, pageInfo.itemsPerPage);
        }
      }
    },
    [items, logEvent, carouselType]
  );

  const handleOnMounted = useCallback(
    (core: CarouselCore) => {
      carousel.current = core;
      setIsCarouselAvailable(true);
      const itemsIndexList: number[] = Array.from(Array(items.length).keys());
      onMounted(itemsIndexList);
    },
    [onMounted, items]
  );
  return (
    <div {...rest}>
      <div className='mb-6 flex items-center justify-between pl-4 lg:pl-7'>
        <h4 className='md'>{title}</h4>
        {viewAllPath && (
          <TextLink
            className='mr-4 text-lg font-bold md:mr-[52px]'
            href={viewAllPath}
            data-test-id='view-all-link'
          >
            View all
          </TextLink>
        )}
      </div>
      <div className='relative'>
        <Carousel onMounted={handleOnMounted}>
          <div className=' 2xl:px-7'>
            <CarouselHorizontalList
              className='pl-4 lg:pl-7 2xl:pl-0'
              data-test-id='list'
            >
              {React.Children.map(children, (child, idx) => {
                return (
                  <CarouselSlideItem
                    key={child?.key}
                    index={idx}
                    className='relative'
                    onClick={() => {
                      handleCardClick(idx);
                    }}
                  >
                    {child}
                  </CarouselSlideItem>
                );
              })}
            </CarouselHorizontalList>
          </div>

          <div className='hidden lg:block'>
            <CarouselPrevious
              className={clsxm(
                'absolute left-3  h-9 w-9',
                navigationButtonTopCss
              )}
              aria-label='Show previous'
              tabIndex={-1}
              data-test-id='previous'
              as={CarouselArrowButton}
            >
              <Icon name='arrowLeft' height={16} width={16} />
            </CarouselPrevious>
          </div>

          <div className='hidden lg:block'>
            <CarouselNext
              className={clsxm(
                'absolute right-3 h-9 w-9',
                navigationButtonTopCss
              )}
              aria-label='Show next'
              tabIndex={-1}
              data-test-id='next'
              as={CarouselArrowButton}
            >
              <Icon name='arrowRight' height={16} width={16} />
            </CarouselNext>
          </div>
        </Carousel>
      </div>
    </div>
  );
};

export default ShopCarousel;
